import React, { useState, useEffect } from 'react';
import supabase from "../../../supabaseClient";
import './GroupGrid.css';
import GroupDetailsModal from "./GroupDetailsModal";

const initialGroupState = (currentUser) => ({
  name: '',
  description: '',
  language: '',
  level: '',
  created_by: currentUser?.id,
  category: [],
  capacity: 10,
  schedules: [{ day: '', time: '', duration: 60 }],
  recurring: false,
  group_banner_url: null
});

const GroupGrid = ({
  groups = [],
  groupMemberships = {},
  currentUser,
  onJoinGroup,
  onLeaveGroup
}) => {
  // State
  const [detailsModalGroup, setDetailsModalGroup] = useState(null);
  const [groupSchedules, setGroupSchedules] = useState({});
  const [expandedCategories, setExpandedCategories] = useState({});

  // Helper: Check if user is admin
  const isGroupAdmin = (group) =>
    currentUser && group && group.created_by && currentUser.id === group.created_by;

  // Handle edit group (kept if needed by modal)
  const handleEdit = (group) => {
    // This function is passed to GroupDetailsModal, but actual editing is handled elsewhere
  };

  // Fetch schedules for all groups from the new table
  useEffect(() => {
    const fetchSchedules = async () => {
      if (!groups?.length) return;
      const groupIds = groups.map(g => g.id);
      const { data, error } = await supabase
        .from('group_schedules')
        .select('*')
        .in('group_id', groupIds);
      if (!error && data) {
        // Group schedules by group_id
        const schedulesByGroup = {};
        data.forEach(sch => {
          if (!schedulesByGroup[sch.group_id]) schedulesByGroup[sch.group_id] = [];
          schedulesByGroup[sch.group_id].push(sch);
        });
        setGroupSchedules(schedulesByGroup);
      }
    };
    fetchSchedules();
  }, [groups]);

  const formatScheduleEntry = (schedule) => {
    if (!schedule) return '';
    const parts = [];
    if (schedule.day) parts.push(schedule.day);
    if (schedule.time) {
      const [hourStr, minuteStr = '00'] = schedule.time.split(":");
      let hour = parseInt(hourStr, 10);
      if (!Number.isNaN(hour)) {
        const minutes = minuteStr.padStart(2, '0');
        const ampm = hour >= 12 ? 'PM' : 'AM';
        hour = hour % 12;
        if (hour === 0) hour = 12;
        parts.push(`${hour}:${minutes} ${ampm}`);
      }
    }
    if (schedule.duration) {
      parts.push(`${schedule.duration} min`);
    }
    if (!parts.length) return '';
    return parts.join(' • ');
  };

  const getFrequencyLabel = (group, schedules) => {
    if (!group || !group.recurring) return null;
    const freq = group.frequency;
    if (freq === 'weekly') return 'Weekly';
    if (freq === 'biweekly') return 'Every 2 weeks';
    if (freq === 'monthly') return 'Monthly';
    if (typeof freq === 'string' && freq.trim()) {
      const trimmed = freq.trim();
      return trimmed.charAt(0).toUpperCase() + trimmed.slice(1);
    }
    // Fallback based on number of weekly meetings
    if ((schedules || []).length > 1) return 'Weekly';
    return 'Weekly';
  };

  const truncateDescription = (text, maxChars = 120) => {
    if (!text) return '';
    if (text.length <= maxChars) return text;
    return `${text.slice(0, maxChars).trimEnd()}…`;
  };

  return (
    <div className="group-grid">
      {groups.map(group => {
        const isAdmin = isGroupAdmin(group);
        const isJoined = !!groupMemberships[group.id];
        const schedules = groupSchedules[group.id] || [];
        const hasSchedules = schedules.length > 0;
        const categories = Array.isArray(group.category) ? group.category : [];
        const isCategoriesExpanded = !!expandedCategories[group.id];
        const visibleCategories = isCategoriesExpanded ? categories : categories.slice(0, 2);
        const extraCategories = categories.length - visibleCategories.length;
        const recurringLabel = getFrequencyLabel(group, schedules);
        return (
          <div className="group-card compact" key={group.id}>
            <div className="group-card-header">
              <div className="group-card-avatar-thumb">
                {group.group_banner_url ? (
                  <img src={group.group_banner_url} alt={group.name} className="group-avatar-thumb" />
                ) : (
                  <div className="group-avatar-fallback-thumb">{group.name[0]}</div>
                )}
                {isAdmin && <span className="admin-badge">Admin</span>}
              </div>
              <div className="group-card-title-section">
                <div className="group-card-title">{group.name}</div>
              </div>
            </div>
            <div className="group-card-body">
              <div className="group-card-meta">
                <div className="group-meta-row primary">
                  {group.language && (
                    <span className="group-meta-chip">{group.language}</span>
                  )}
                  {group.level && (
                    <span className="group-meta-chip">{group.level}</span>
                  )}
                  {group.capacity && (
                    <span className="group-meta-chip">
                      {group.capacity} members
                    </span>
                  )}
                </div>
                {(visibleCategories.length > 0 || extraCategories > 0) && (
                  <div className="group-meta-row categories">
                    {visibleCategories.map((cat) => (
                      <span className="category-badge" key={cat}>
                        {cat}
                      </span>
                    ))}
                    {extraCategories > 0 && !isCategoriesExpanded && (
                      <button
                        type="button"
                        className="category-badge more-toggle"
                        onClick={() =>
                          setExpandedCategories(prev => ({ ...prev, [group.id]: true }))
                        }
                      >
                        +{extraCategories} more
                      </button>
                    )}
                    {isCategoriesExpanded && categories.length > 2 && (
                      <button
                        type="button"
                        className="category-badge more-toggle collapse"
                        onClick={() =>
                          setExpandedCategories(prev => {
                            const next = { ...prev };
                            delete next[group.id];
                            return next;
                          })
                        }
                      >
                        Show less
                      </button>
                    )}
                  </div>
                )}
                {hasSchedules && (
                  <div className="group-meta-row schedule">
                    {recurringLabel && (
                      <span className="group-meta-chip recurring-chip">
                        {recurringLabel}
                      </span>
                    )}
                    {schedules.map((sch, idx) => (
                      <span
                        className="schedule-badge"
                        key={sch.id || `${group.id}-${idx}`}
                      >
                        {formatScheduleEntry(sch)}
                      </span>
                    ))}
                  </div>
                )}
              </div>
              {group.description && (
                <div className="group-card-description">
                  {truncateDescription(group.description)}
                </div>
              )}
            </div>
            <div className="group-card-actions">
              <button
                className="see-details-btn"
                type="button"
                onClick={() => setDetailsModalGroup(group)}
              >
                See more details
              </button>
              {isJoined ? (
                <button className="join-btn joined" type="button" disabled>
                  Joined
                </button>
              ) : (
                <button
                  className="join-btn"
                  type="button"
                  onClick={() => onJoinGroup(group.id)}
                >
                  Join
                </button>
              )}
            </div>
          </div>
        );
      })}
      {detailsModalGroup && (
        <GroupDetailsModal
          group={detailsModalGroup}
          isAdmin={isGroupAdmin(detailsModalGroup)}
          isJoined={!!groupMemberships[detailsModalGroup.id]}
          schedules={groupSchedules[detailsModalGroup.id] || []}
          onClose={() => setDetailsModalGroup(null)}
          onEdit={handleEdit}
          onDelete={() => {}}
          onJoin={onJoinGroup}
          onLeave={onLeaveGroup}
        />
      )}
    </div>
  );
};

export default GroupGrid;